/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';
/* eslint-disable prefer-arrow-callback */

import { getLayoutData, makeGetWidgetContextualData } from 'DesignData/selectors/layoutData';
import { isSectionDelimiterType } from 'DesignData/helpers/widgets';
import ValidatedLayoutGroup from 'DesignData/models/layouts/layoutGrouping/ValidatedLayoutGroup';
import LayoutGroupingErrorTypes from 'DesignData/enums/LayoutGroupingErrorTypes';
import { isMatch, isEqual, findWhere, once } from 'underscore';
import I18n from 'I18n';
/*
 * Example groupingMatrix
 */

/*
const groupingMatrix = {
  parentId: 'w99',
  rows: [
    {
      rowId: 'row_11',
      groups: [
        {
          x: 0,
          w: 4,
          isSelected: false,
          widgets: ['w1', 'w2'],
        },
        {
          x: 4,
          w: 6,
          isSelected: true,
          widgets: ['w3', 'w4'],
        },
        {
          x: 10,
          w: 2,
          isSelected: false,
          widgets: ['w5'],
        },
      ],
    },
    {
      rowId: 'row_12',
      groups: [
        {
          x: 0,
          w: 4,
          isSelected: false,
          widgets: ['w6'],
        },
        {
          x: 4,
          w: 6,
          isSelected: true,
          widgets: ['w7'],
        },
        {
          x: 10,
          w: 2,
          isSelected: false,
          widgets: ['w8', 'w9'],
        },
      ],
    },
  ],
};
*/

const getWidgetContextualData = makeGetWidgetContextualData();
/**
 * @param {Array} arr            List to find selections within.
 * @param {Object} selectionSet  Set of selected items.
 * @param {String} segmentName   Name to be used for segment lists.
 * @return {Array.<Object>}
 */

function getSelectionSegmentsForList(arr, selectionSet, segmentName = 'items') {
  const selections = [];
  arr.reduce((last, id) => {
    const isSelected = !!selectionSet[id];

    if (last.isSelected === isSelected) {
      last[segmentName].push(id);
    } else {
      last = {
        isSelected,
        [segmentName]: [id]
      };
      selections.push(last);
    }

    return last;
  }, {});
  return selections;
}
/**
 * TODO: This could be useful elsewhere as a general util function!!!!
 *
 * Pattern match an array.
 *
 * @param {Array} arr
 * @param {Array} patternArr
 * @return {Object} An object with the matches and index from the source array {index: 2, matches: [{},{},{}]}
 */


function patternMatchList(arr, patternArr) {
  const {
    length: patternLength
  } = patternArr;
  const matches = [];
  let index = -1;
  arr.some((val, idx) => {
    if (matches.length === patternLength) {
      return true;
    }

    const evaluate = val === Object(val) ? isMatch : isEqual;

    if (evaluate(val, patternArr[matches.length])) {
      if (!~index) {
        index = idx;
      }

      matches.push(val);
    } else {
      matches.length = 0;
      index = -1;
    }

    return false;
  });

  if (matches.length !== patternLength) {
    matches.length = 0;
    index = -1;
  }

  return {
    index,
    matches
  };
}

const GAP_PATTERN = [{
  isSelected: true
}, {
  isSelected: false
}, {
  isSelected: true
}];
const getValidationMessages = once(function getValidationMessages() {
  return Object.keys(LayoutGroupingErrorTypes).reduce((hash, key) => {
    hash[key] = I18n.text(`designdata.layoutWidgetGrouping.errors.${key}`);
    return hash;
  }, {});
});
/**
 * Validate grouping selections.
 * Build a grouping matrix of all rows/widgets affected.
 * Return a ValidatedLayoutGroup instance.
 *
 * @param {Object} state
 * @param {Number} layoutId
 * @param {Array}  widgetList
 * @return {ValidatedLayoutGroup}
 */

export function validate(state, layoutId, widgetList) {
  const validationMessages = getValidationMessages();

  const createInvalidValidatedLayoutGroup = errorType => {
    return new ValidatedLayoutGroup({
      isValid: false,
      errorType,
      errorMessage: validationMessages[errorType]
    });
  };
  /*
   * Must have at least one widget selected.
   */


  if (!widgetList.length) {
    return createInvalidValidatedLayoutGroup(LayoutGroupingErrorTypes.NO_SELECTION);
  } // Hash of widgets and their contextual data.


  const selections = widgetList.reduce((hash, widgetId) => {
    hash[widgetId] = getWidgetContextualData(state, {
      layoutId,
      widgetId
    });
    return hash;
  }, {});
  const {
    widgets,
    rows
  } = getLayoutData(state, {
    layoutId
  });
  const {
    parentId: parentWidgetId
  } = selections[widgetList[0]];
  const parentWidget = widgets[parentWidgetId];
  /*
   * Iterate over widgetList and evaluate each widget.
   *
   * 1. Widget must not be the root widget.
   * 2. Widget must not be within a Flex Column.
   * 3. Widget must not be a section delimiter.
   * 4. All selected nodes in the group must be at the same depth in the tree, thus they must all have the same parent.
   */

  const selectedRowsSet = {};
  let isAllParentsTheSame = true;
  let isRootWidgetIncluded = false;
  let isFlexColumnChildIncluded = false;
  let isSectionDelimiterIncluded = false;
  widgetList.every(widgetId => {
    const {
      rowId,
      parentId,
      isRoot,
      isInFlexColumn
    } = selections[widgetId];

    if (isRoot) {
      isRootWidgetIncluded = true;
      return false;
    }

    if (isInFlexColumn) {
      isFlexColumnChildIncluded = true;
      return false;
    }

    if (isSectionDelimiterType(widgets[widgetId].type)) {
      isSectionDelimiterIncluded = true;
      return false;
    }

    if (parentWidget && parentId !== parentWidget.id) {
      isAllParentsTheSame = false;
      return false;
    }

    selectedRowsSet[rowId] = rowId;
    return true;
  });

  if (isRootWidgetIncluded) {
    return createInvalidValidatedLayoutGroup(LayoutGroupingErrorTypes.SELECTIONS_INCLUDE_ROOT);
  }

  if (isFlexColumnChildIncluded) {
    return createInvalidValidatedLayoutGroup(LayoutGroupingErrorTypes.SELECTIONS_WITHIN_FLEX_COLUMN);
  }

  if (isSectionDelimiterIncluded) {
    return createInvalidValidatedLayoutGroup(LayoutGroupingErrorTypes.SELECTIONS_INCLUDE_SECTION_DELIMITER);
  }

  if (!isAllParentsTheSame) {
    return createInvalidValidatedLayoutGroup(LayoutGroupingErrorTypes.SELECTIONS_FROM_MULTIPLE_NODES);
  }
  /*
   * Selections must be in consecutive rows.
   * Selections cannot span section delimiters.
   */


  const selectedRowSegments = getSelectionSegmentsForList(parentWidget.rows, selectedRowsSet, 'rows');
  const gapRowMatches = patternMatchList(selectedRowSegments, GAP_PATTERN);
  const gapRows = ~gapRowMatches.index ? gapRowMatches.matches[1].rows : [];

  if (gapRows.length) {
    const isSectionDelimiterRow = rowId => {
      const firstWidget = widgets[rows[rowId][0]];
      return isSectionDelimiterType(firstWidget.type);
    };

    const isSpanningSectionDelimiter = gapRows.some(isSectionDelimiterRow);
    const errorType = isSpanningSectionDelimiter ? LayoutGroupingErrorTypes.SELECTIONS_CROSS_SECTION_DELIMITERS : LayoutGroupingErrorTypes.SELECTIONS_SKIP_ROWS;
    return createInvalidValidatedLayoutGroup(errorType);
  }
  /*
   * Setup groupingMatrix
   */


  const {
    rows: selectedRowsList
  } = findWhere(selectedRowSegments, {
    isSelected: true
  });
  const groupingMatrix = {
    parentId: parentWidget.id,
    rows: []
  };
  /*
   * Selections must be in consecutive order within a row.
   * Selections must form a perfect rectangle based on their `x` and `w` values.
   *
   * Fill in rows of groupingMatrix as we validate.
   */

  let hasNonConsecutiveWidgets = false;
  let hasNonRectangularSelection = false;
  {
    const getWidget = widgetId => widgets[widgetId];

    let compareX = null;
    let compareW = null;
    selectedRowsList.some(rowId => {
      const row = rows[rowId];
      const groups = getSelectionSegmentsForList(row, selections, 'widgets');
      const selectedGroups = groups.filter(group => group.isSelected); // If more than one group of selected widgets in a row, then we have gaps in the selections.

      const rowHasSelectionGaps = selectedGroups.length !== 1;

      if (rowHasSelectionGaps) {
        hasNonConsecutiveWidgets = true;
        return true;
      }

      hasNonRectangularSelection = groups.some(group => {
        const {
          x,
          w
        } = group.widgets.map(getWidget).reduce((coords, widget, idx) => {
          if (idx === 0) {
            coords.x = widget.x;
          }

          coords.w += widget.w;
          return coords;
        }, {
          x: 0,
          w: 0
        });
        group.x = x;
        group.w = w;

        if (group.isSelected) {
          if (compareX === null && compareW === null) {
            compareX = x;
            compareW = w;
          } else if (compareX !== x || compareW !== w) {
            hasNonRectangularSelection = true;
            return true;
          }
        }

        return false;
      });

      if (hasNonRectangularSelection) {
        return true;
      } // Validated as ok, so add to matrix.


      groupingMatrix.rows.push({
        rowId,
        groups
      });
      return false;
    });
  }

  if (hasNonConsecutiveWidgets || hasNonRectangularSelection) {
    return createInvalidValidatedLayoutGroup(LayoutGroupingErrorTypes.SELECTIONS_NOT_RECTANGULAR);
  }
  /*
   * Validation passed for all selections.
   */


  return new ValidatedLayoutGroup({
    isValid: true,
    errorType: null,
    errorMessage: null,
    groupingMatrix
  });
}